import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import DataTable from '../../components/design/DataTable.jsx';
import { collectYoutubeAnalytics, getYoutubeGrowthInsights, listYoutubeAnalytics, listYoutubeChannels, listYoutubeUploads, runYoutubeGrowthInsights } from '../../services/contentAiService';

function fmtDay(d) {
  if (!d) return '';
  try {
    const dt = new Date(String(d));
    if (Number.isFinite(dt.getTime())) return dt.toISOString().slice(0, 10);
  } catch {}
  return String(d).slice(0, 10);
}

function lastDaysRange(days) {
  const d = Math.max(3, Math.min(90, Number(days || 30)));
  const end = new Date();
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  start.setUTCDate(start.getUTCDate() - (d - 1));
  return { from: start.toISOString(), to: end.toISOString() };
}

export default function YouTubeAnalyticsPage() {
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [channels, setChannels] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [rows, setRows] = useState([]);
  const [growth, setGrowth] = useState(null);

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [ch, up] = await Promise.all([
        listYoutubeChannels(),
        listYoutubeUploads(200)
      ]);
      setChannels(Array.isArray(ch?.data) ? ch.data : []);
      setUploads(Array.isArray(up?.data) ? up.data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const kind = tab === 0 ? 'channel' : (tab === 1 ? 'video' : 'video_daily');
      const range = tab === 2 ? lastDaysRange(30) : null;
      const resp = await listYoutubeAnalytics({
        limit: tab === 2 ? 1000 : 300,
        kind,
        youtubeChannelId: selectedChannelId ? Number(selectedChannelId) : undefined
        ,
        ...(tab === 2 && selectedVideoId ? { youtubeVideoId: String(selectedVideoId) } : {}),
        ...(tab === 2 && range ? { from: range.from, to: range.to } : {})
      });
      const data = resp?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar analytics');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, selectedVideoId, tab]);

  const loadGrowth = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      const resp = await getYoutubeGrowthInsights(Number(selectedChannelId));
      setGrowth(resp?.data || null);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar growth insights');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId]);

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  useEffect(() => {
    if (tab === 3) loadGrowth();
    else loadAnalytics();
  }, [loadAnalytics, loadGrowth, tab]);

  const uploadByVideoId = useMemo(() => {
    const map = new Map();
    for (const u of uploads || []) {
      if (u.youtubeVideoId) map.set(String(u.youtubeVideoId), u);
    }
    return map;
  }, [uploads]);

  const columnsChannel = [
    { field: 'collectedAt', headerName: 'Coletado', width: 190, valueGetter: (p) => (p?.row?.collectedAt ? new Date(p.row.collectedAt).toLocaleString() : '-') },
    { field: 'title', headerName: 'Canal', flex: 1, minWidth: 260, valueGetter: (p) => (p?.row?.metricsJson?.snippet?.title || '-') },
    { field: 'subscribers', headerName: 'Inscritos', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.statistics?.subscriberCount || '-') },
    { field: 'views', headerName: 'Views', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.statistics?.viewCount || '-') },
    { field: 'videos', headerName: 'Vídeos', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.statistics?.videoCount || '-') }
  ];

  const columnsVideo = [
    { field: 'collectedAt', headerName: 'Coletado', width: 190, valueGetter: (p) => (p?.row?.collectedAt ? new Date(p.row.collectedAt).toLocaleString() : '-') },
    { field: 'youtubeVideoId', headerName: 'YouTube ID', width: 200, valueGetter: (p) => (p?.row?.youtubeVideoId || p?.row?.metricsJson?.youtubeVideoId || '-') },
    { field: 'title', headerName: 'Título', flex: 1, minWidth: 320, valueGetter: (p) => (p?.row?.metricsJson?.snippet?.title || '-') },
    { field: 'views', headerName: 'Views', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.statistics?.viewCount || '-') },
    { field: 'likes', headerName: 'Likes', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.statistics?.likeCount || '-') },
    { field: 'comments', headerName: 'Comentários', width: 130, valueGetter: (p) => (p?.row?.metricsJson?.statistics?.commentCount || '-') },
    {
      field: 'localUpload',
      headerName: 'Upload',
      width: 120,
      valueGetter: (p) => {
        const yid = p?.row?.youtubeVideoId || p?.row?.metricsJson?.youtubeVideoId || null;
        const u = yid ? uploadByVideoId.get(String(yid)) : null;
        return u ? `#${u.id}` : '-';
      }
    }
  ];

  const dailyPoints = useMemo(() => {
    if (tab !== 2) return [];
    const pts = (Array.isArray(rows) ? rows : [])
      .map((r) => {
        const day = r?.metricsJson?.day || (r?.collectedAt ? fmtDay(r.collectedAt) : null);
        const m = r?.metricsJson?.metrics || {};
        return {
          day: String(day || ''),
          views: Number(m.views || 0),
          likes: Number(m.likes || 0),
          comments: Number(m.comments || 0),
          shares: Number(m.shares || 0),
          minutesWatched: Number(m.estimatedMinutesWatched || 0),
          avgViewDuration: Number(m.averageViewDuration || 0),
          retentionPct: Number(m.averageViewPercentage || 0),
          impressions: Number(m.impressions || 0),
          ctrPct: Number(m.impressionsCtr || 0)
        };
      })
      .filter((p) => p.day)
      .sort((a, b) => String(a.day).localeCompare(String(b.day)));
    return pts;
  }, [rows, tab]);

  const dailyVideos = useMemo(() => {
    const out = [];
    for (const u of uploads || []) {
      if (!u.youtubeVideoId) continue;
      if (selectedChannelId && Number(u.youtubeChannelId) !== Number(selectedChannelId)) continue;
      out.push({ youtubeVideoId: String(u.youtubeVideoId), title: u.title || u.youtubeVideoId });
    }
    return out.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
  }, [uploads, selectedChannelId]);

  const onCollectNow = useCallback(async () => {
    setLoading(true);
    try {
      await collectYoutubeAnalytics();
      toast.success('Coleta iniciada');
      await loadAnalytics();
    } catch (e) {
      toast.error(e?.message || 'Falha ao coletar analytics');
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  const onRunGrowth = useCallback(async () => {
    if (!selectedChannelId) {
      toast.error('Selecione um canal');
      return;
    }
    setLoading(true);
    try {
      await runYoutubeGrowthInsights(Number(selectedChannelId));
      toast.success('Growth Intelligence executada');
      await loadGrowth();
    } catch (e) {
      toast.error(e?.message || 'Falha ao executar growth intelligence');
    } finally {
      setLoading(false);
    }
  }, [loadGrowth, selectedChannelId]);

  const growthRows = useMemo(() => {
    const rs = growth?.rows || [];
    return Array.isArray(rs) ? rs.slice().sort((a, b) => Number(a.windowDays || 0) - Number(b.windowDays || 0)) : [];
  }, [growth]);

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5">YouTube Analytics</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="outlined" onClick={loadBase} disabled={loading}>Atualizar base</Button>
          <Button variant="contained" onClick={tab === 3 ? loadGrowth : loadAnalytics} disabled={loading}>{tab === 3 ? 'Atualizar growth' : 'Atualizar analytics'}</Button>
          <Button variant="contained" color="secondary" onClick={onCollectNow} disabled={loading}>Coletar agora</Button>
          <Button variant="contained" color="secondary" onClick={onRunGrowth} disabled={loading || !selectedChannelId}>Executar Growth</Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Canal" />
        <Tab label="Vídeos" />
        <Tab label="Diário" />
        <Tab label="Growth" />
      </Tabs>

      <FormControl fullWidth>
        <InputLabel>Canal</InputLabel>
        <Select label="Canal" value={selectedChannelId} onChange={(e) => setSelectedChannelId(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {channels.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>{c.name || c.channelId || `Canal ${c.id}`}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {tab === 3 ? (
        <Box display="grid" gap={2}>
          {!selectedChannelId ? (
            <Typography>Selecione um canal para ver Growth Intelligence.</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Baseado em dados reais de analytics (video_daily) associados ao canal. Último asOfDate: {growth?.latestAsOfDate || '-'}
              </Typography>
              {growthRows.map((gi) => {
                const insights = gi?.insightsJson || {};
                const recs = Array.isArray(gi?.recommendationsJson) ? gi.recommendationsJson : [];
                const bestHours = Array.isArray(insights?.bestHours) ? insights.bestHours : [];
                const bestDur = Array.isArray(insights?.bestDurations) ? insights.bestDurations : [];
                const topVideos = Array.isArray(insights?.topVideos) ? insights.topVideos : [];
                return (
                  <Card key={`${gi.id}`} variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2} alignItems="center">
                        <Typography variant="h6">Janela {gi.windowDays} dias</Typography>
                        <Typography variant="body2" color="text.secondary">Status: {gi.status} • asOf: {gi.asOfDate}</Typography>
                      </Box>

                      <Box display="grid" gap={2} mt={2}>
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle1">Melhores horários (UTC)</Typography>
                              <Box height={220}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={bestHours}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="hour" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="avgCtr" name="CTR (avg)" fill="#ff9800" />
                                    <Bar dataKey="views" name="Views" fill="#2196f3" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </Box>
                            </CardContent>
                          </Card>

                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle1">Melhores durações</Typography>
                              <Box height={220}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={bestDur}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="bucket" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="avgCtr" name="CTR (avg)" fill="#ff9800" />
                                    <Bar dataKey="avgRetention" name="Retenção (avg)" fill="#9c27b0" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>

                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1">Recomendações</Typography>
                            <Box display="grid" gap={1} mt={1}>
                              {recs.length ? recs.map((r, idx) => (
                                <Box key={idx}>
                                  <Typography variant="body2"><b>{r.title || r.key}</b></Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {(Array.isArray(r.recommendations) ? r.recommendations : []).filter(Boolean).join(' • ') || '-'}
                                  </Typography>
                                </Box>
                              )) : (
                                <Typography variant="body2" color="text.secondary">Sem recomendações</Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>

                        <DataTable
                          rows={topVideos}
                          loading={loading}
                          columns={[
                            { field: 'title', headerName: 'Título', flex: 1, minWidth: 320, valueGetter: (p) => (p?.row?.title || '-') },
                            { field: 'views', headerName: 'Views', width: 120, valueGetter: (p) => (p?.row?.views ?? '-') },
                            { field: 'ctr', headerName: 'CTR', width: 120, valueGetter: (p) => (p?.row?.ctr != null ? `${Number(p.row.ctr).toFixed(2)}%` : '-') },
                            { field: 'ret', headerName: 'Retenção', width: 140, valueGetter: (p) => (p?.row?.retention != null ? `${Number(p.row.retention).toFixed(1)}%` : '-') },
                            { field: 'mins', headerName: 'Watch time (min)', width: 160, valueGetter: (p) => (p?.row?.minutesWatched ?? '-') },
                            { field: 'hour', headerName: 'Hora UTC', width: 110, valueGetter: (p) => (p?.row?.hourUtc != null ? `${p.row.hourUtc}h` : '-') },
                            { field: 'dur', headerName: 'Duração', width: 120, valueGetter: (p) => (p?.row?.durationSeconds != null ? `${Math.round(Number(p.row.durationSeconds) / 60)}m` : '-') }
                          ]}
                          getRowId={(r) => r.youtubeVideoId}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </Box>
      ) : tab === 2 ? (
        <Box display="grid" gap={2}>
          <FormControl fullWidth>
            <InputLabel>Vídeo</InputLabel>
            <Select label="Vídeo" value={selectedVideoId} onChange={(e) => setSelectedVideoId(e.target.value)}>
              <MenuItem value="">Selecione…</MenuItem>
              {dailyVideos.map((v) => (
                <MenuItem key={v.youtubeVideoId} value={v.youtubeVideoId}>{v.title}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">Views / Watch time</Typography>
              <Box height={280}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyPoints}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="views" stroke="#2196f3" dot={false} />
                    <Line type="monotone" dataKey="minutesWatched" stroke="#4caf50" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6">CTR / Retenção / Duração média</Typography>
              <Box height={280}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyPoints}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="ctrPct" stroke="#ff9800" dot={false} />
                    <Line type="monotone" dataKey="retentionPct" stroke="#9c27b0" dot={false} />
                    <Line type="monotone" dataKey="avgViewDuration" stroke="#607d8b" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <DataTable
            rows={rows}
            loading={loading}
            columns={[
              { field: 'day', headerName: 'Dia', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.day || (p?.row?.collectedAt ? fmtDay(p.row.collectedAt) : '-')) },
              { field: 'views', headerName: 'Views', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.metrics?.views ?? '-') },
              { field: 'likes', headerName: 'Likes', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.metrics?.likes ?? '-') },
              { field: 'comments', headerName: 'Comments', width: 130, valueGetter: (p) => (p?.row?.metricsJson?.metrics?.comments ?? '-') },
              { field: 'shares', headerName: 'Shares', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.metrics?.shares ?? '-') },
              { field: 'minutes', headerName: 'Watch time (min)', width: 160, valueGetter: (p) => (p?.row?.metricsJson?.metrics?.estimatedMinutesWatched ?? '-') },
              { field: 'ctr', headerName: 'CTR', width: 120, valueGetter: (p) => (p?.row?.metricsJson?.metrics?.impressionsCtr != null ? `${p.row.metricsJson.metrics.impressionsCtr}%` : '-') },
              { field: 'ret', headerName: 'Retenção', width: 140, valueGetter: (p) => (p?.row?.metricsJson?.metrics?.averageViewPercentage != null ? `${p.row.metricsJson.metrics.averageViewPercentage}%` : '-') }
            ]}
            getRowId={(r) => r.id}
          />
        </Box>
      ) : (
        <DataTable
          rows={rows}
          loading={loading}
          columns={tab === 0 ? columnsChannel : columnsVideo}
          getRowId={(r) => r.id}
        />
      )}
    </Box>
  );
}
